import { useState, useCallback } from "react";
import type { CalcRoom, Selections } from "../../../mocks/calculatorData";

interface RoomAccordionProps {
  rooms: CalcRoom[];
  selections: Selections;
  onUpdate: (itemId: string, qty: number) => void;
}

interface ItemCardProps {
  item: CalcRoom["items"][number];
  qty: number;
  accentColor: string;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  isPopping: boolean;
}

function ItemCard({ item, qty, accentColor, onAdd, onRemove, isPopping }: ItemCardProps) {
  const selected = qty > 0;

  return (
    <div className="relative flex flex-col">
      <button
        onClick={() => onAdd(item.id)}
        className={`relative flex flex-col items-center justify-center gap-1.5 rounded-2xl p-3 h-36 w-full thick-border transition-all duration-200 cursor-pointer group ${
          isPopping ? "anim-pop" : ""
        } ${selected ? "cartoon-shadow" : "hover:cartoon-shadow-sm"}`}
        style={
          selected
            ? {
                background: accentColor,
                borderColor: "#0f172a",
                transform: "translate(-2px,-2px)",
              }
            : { background: "#fff" }
        }
        aria-label={`Add ${item.label}`}
      >
        {/* Count badge */}
        {selected && (
          <span
            className="absolute top-2 left-2.5 w-7 h-7 flex items-center justify-center rounded-full text-xs font-black anim-bounce-in"
            style={{ background: "#fff", color: accentColor }}
          >
            {qty}
          </span>
        )}

        {/* Icon */}
        <i
          className={`${item.icon} transition-all duration-200`}
          style={{
            fontSize: "2rem",
            color: selected ? "#fff" : accentColor,
            filter: selected ? "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" : "none",
          }}
        ></i>

        {/* Label */}
        <span
          className="text-xs font-black text-center leading-tight px-1 line-clamp-2"
          style={{ color: selected ? "#fff" : "#09245b" }}
        >
          {item.label}
        </span>

        {/* Volume */}
        <span
          className="text-[10px] font-bold"
          style={{ color: selected ? "rgba(255,255,255,0.7)" : "#9ca3af" }}
        >
          {item.volume} yd³
        </span>

        {/* Hover hint when empty */}
        {!selected && (
          <span className="absolute bottom-2 right-2 w-5 h-5 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ background: accentColor }}
          >
            <i className="ri-add-line text-white" style={{ fontSize: "10px" }}></i>
          </span>
        )}
      </button>

      {/* Remove / decrement button */}
      {selected && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
          className="absolute top-2 right-2.5 w-7 h-7 flex items-center justify-center rounded-full transition-all cursor-pointer hover:scale-110"
          style={{ background: "rgba(255,255,255,0.25)", border: "1.5px solid rgba(255,255,255,0.5)" }}
          aria-label={`Remove ${item.label}`}
        >
          <i className="ri-subtract-line text-white" style={{ fontSize: "12px" }}></i>
        </button>
      )}
    </div>
  );
}

interface RoomCardProps {
  room: CalcRoom;
  selections: Selections;
  onUpdate: (itemId: string, qty: number) => void;
}

function RoomCard({ room, selections, onUpdate }: RoomCardProps) {
  const [open, setOpen] = useState(false);
  const [poppingIds, setPoppingIds] = useState<Set<string>>(new Set());

  const roomQty = room.items.reduce((s, it) => s + (selections[it.id] ?? 0), 0);
  const roomVol = room.items.reduce((s, it) => s + (selections[it.id] ?? 0) * it.volume, 0);

  const handleAdd = useCallback((id: string) => {
    const current = selections[id] ?? 0;
    onUpdate(id, current + 1);
    setPoppingIds((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setPoppingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 380);
  }, [selections, onUpdate]);

  const handleRemove = useCallback((id: string) => {
    const current = selections[id] ?? 0;
    if (current > 0) onUpdate(id, current - 1);
  }, [selections, onUpdate]);

  return (
    <div
      className={`rounded-3xl overflow-hidden thick-border transition-all duration-250 ${
        open ? "cartoon-shadow" : ""
      }`}
    >
      {/* Room header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-4 px-5 py-4 cursor-pointer text-left transition-all duration-200"
        style={{ background: open ? room.accentColor : "#fff" }}
      >
        {/* Big room icon */}
        <span
          className="w-14 h-14 flex items-center justify-center rounded-2xl flex-shrink-0 thick-border transition-all duration-200"
          style={{
            background: open ? "rgba(255,255,255,0.22)" : room.bgColor,
            borderColor: open ? "rgba(255,255,255,0.4)" : "#0f172a",
          }}
        >
          <i
            className={`${room.icon} text-2xl`}
            style={{ color: open ? "#fff" : room.accentColor }}
          ></i>
        </span>

        <div className="flex-1 min-w-0">
          <p
            className="font-black text-lg leading-tight"
            style={{ color: open ? "#fff" : "#09245b" }}
          >
            {room.label}
          </p>
          {roomQty > 0 ? (
            <p
              className="text-xs font-bold mt-0.5"
              style={{ color: open ? "rgba(255,255,255,0.8)" : room.accentColor }}
            >
              {roomQty} item{roomQty !== 1 ? "s" : ""} ·{" "}
              {roomVol.toFixed(1)} yd³ added
            </p>
          ) : (
            <p
              className="text-xs font-bold mt-0.5"
              style={{ color: open ? "rgba(255,255,255,0.6)" : "#9ca3af" }}
            >
              {room.items.length} items to choose from — tap to expand
            </p>
          )}
        </div>

        {roomQty > 0 && (
          <span
            className="w-10 h-10 flex items-center justify-center rounded-full text-base font-black flex-shrink-0 transition-all anim-bounce-in"
            style={{
              background: open ? "rgba(255,255,255,0.9)" : room.accentColor,
              color: open ? room.accentColor : "#fff",
            }}
          >
            {roomQty}
          </span>
        )}

        <span
          className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300 ${
            open ? "rotate-180" : ""
          }`}
          style={{ background: open ? "rgba(255,255,255,0.18)" : "#f4f4f4" }}
        >
          <i
            className="ri-arrow-down-s-line text-xl"
            style={{ color: open ? "#fff" : "#09245b" }}
          ></i>
        </span>
      </button>

      {/* Items grid */}
      {open && (
        <div
          className="px-5 pb-5 pt-4 anim-slide-up"
          style={{ background: room.bgColor + "66" }}
        >
          <p className="text-xs font-black text-cl-dark/50 uppercase tracking-widest mb-4">
            Tap items to add them to your van
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {room.items.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                qty={selections[item.id] ?? 0}
                accentColor={room.accentColor}
                onAdd={handleAdd}
                onRemove={handleRemove}
                isPopping={poppingIds.has(item.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RoomAccordion({ rooms, selections, onUpdate }: RoomAccordionProps) {
  return (
    <div className="flex flex-col gap-3">
      {rooms.map((room) => (
        <RoomCard
          key={room.id}
          room={room}
          selections={selections}
          onUpdate={onUpdate}
        />
      ))}
    </div>
  );
}
